import type { GraphModification, Graph } from "@lr-notitia/notitia-engineer/schemas";
import type { ConceptView } from "@lr-notitia/notitia-model-store/schemas";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptViews = (graph: Ref<Graph>) =>
	computed<ConceptView[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "view" && node.layer === "concept"
			) as unknown as ConceptView[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptView(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => graph.value.nodes[id] as ConceptView | undefined,
		add: (refersTo: string, localTerminology: Terminology, tags?: string[]) =>
			add(domainId, graph, refersTo, localTerminology, tags),
		update: (view: ConceptView) => update(domainId, graph, view),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	refersTo: string,
	localTerminology: Terminology,
	tags: string[] = []
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.infmodel()
			.views()
			.post({ refersTo, localTerminology, tags })
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, view: ConceptView): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[view.id]) return reject();
		api()
			.domain(domainId.value)
			.infmodel()
			.view(view.id)
			.put(view)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.infmodel()
			.view(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
